// Layout imbriqué pour /console et /console/plugins.
// Gère l'auth check + fournit WsProvider + ConfirmProvider + ConsoleShell.
// Les routes enfants (Hosts, Plugins) sont rendues via props.children.

import { onMount, createSignal, Show, type JSX } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { ConsoleShell } from "../../operator/console/console-shell";
import { BugReportWidget } from "../../operator/shared/bug-report-widget";
import { ConfirmProvider } from "../../operator/shared/confirm-dialog";
import { NotifyProvider } from "../../operator/shared/notify";
import { WsProvider } from "../../operator/shared/ws";

import.meta.glob("../../operator/panels/*.tsx", { eager: true });

interface SessionData {
  user: { id: string; email: string; name: string; roles: string[] };
}

export default function ConsoleLayout(props: { children?: JSX.Element }) {
  const navigate = useNavigate();
  const [session, setSession] = createSignal<SessionData | null>(null);
  const [checked, setChecked] = createSignal(false);

  onMount(async () => {
    try {
      const r = await fetch("/api/auth/get-session", { credentials: "include" });
      if (!r.ok) {
        navigate("/login", { replace: true });
        return;
      }
      const data = (await r.json()) as SessionData | null;
      if (!data?.user) {
        navigate("/login", { replace: true });
        return;
      }
      // Normaliser BetterAuth : le plugin admin expose `role` (string) tandis
      // que le custom field est `roles` (string[]). S'assurer que le frontend
      // voit toujours un tableau.
      const u = data.user as any;
      if (!Array.isArray(u.roles) && typeof u.role === "string") {
        u.roles = [u.role];
      }
      setSession(data);
    } catch (_err) {
      navigate("/login", { replace: true });
    } finally {
      setChecked(true);
    }
  });

  return (
    <Show
      when={checked() && session()}
      fallback={
        <div
          style={{
            display: "flex",
            "align-items": "center",
            "justify-content": "center",
            "min-height": "100vh",
            background: "#0f172a",
            color: "#94a3b8",
            "font-family": "system-ui, sans-serif",
          }}
        >
          Vérification session…
        </div>
      }
    >
      <WsProvider>
        <NotifyProvider>
          <ConfirmProvider>
            <ConsoleShell userEmail={session()!.user.email} userRoles={session()!.user.roles}>
              {props.children}
            </ConsoleShell>
            <BugReportWidget />
          </ConfirmProvider>
        </NotifyProvider>
      </WsProvider>
    </Show>
  );
}
